import jwtDecode from 'jwt-decode';
import React, { Context, createContext, useContext, useEffect, useState } from 'react';
import { api } from '../../api';
import { Profile } from '../../data/Profile';
import { useStorage } from '../../hooks/useStorage';

export type Jwt = {
    token: string;
    profile: Profile;
};

export const initialJwtValue: Jwt = {
    token: '',
    profile: {
        sub: '',
        iss: '',
        aud: '',
        jti: '',
        userId: '',
        account: '',
        userName: '',
        email: '',
        picture: '',
        authority: -1,
        iat: -1,
        exp: -1,
    },
};

export type Auth = Jwt & {
    signIn: (token: string) => void;
    signOut: () => void;
    isAuth: boolean;
};

export const AuthContext: Context<Auth> = createContext({
    ...initialJwtValue,
    signIn: () => {},
    signOut: () => {},
    isAuth: false,
} as Auth);

interface AuthProviderProps {
    children: React.ReactNode;
}

export function AuthProvider(props: AuthProviderProps): React.ReactElement {
    const { children } = props;
    const storage = useStorage();
    const [auth, setAuth] = useState(() => storage.getAuth());
    const [token, setToken] = useState(() => storage.getAuth().token);
    const [isAuth, setIsAuth] = useState(false);
    const signIn = (token: string) => setToken(token);
    const signOut = () => {
        setToken('');
    };

    useEffect(() => {
        if (token && token.length > 0) {
            api.auth.profile(token).then((ret) => setIsAuth(ret.sub.length > 0 ? true : false));
            const profile: Profile = token ? jwtDecode(token) : initialJwtValue.profile;
            storage.setAuth({ token: token, profile: profile });
            setAuth({ token: token, profile: profile });
        } else {
            setIsAuth(false);
            const profile: Profile = initialJwtValue.profile;
            storage.setAuth({ token: token, profile: profile });
            setAuth({ token: token, profile: profile });
        }
    }, [token]);

    return <AuthContext.Provider value={{ ...auth, signIn, signOut, isAuth }}>{children}</AuthContext.Provider>;
}

export const useAuth = (): Auth => useContext(AuthContext);
